import * as React from "react";
import { Alert, AlertTitle, Card, Stack } from "@mui/material";
import { useGetCclParticipantsByEventKeyQuery } from "../../../../services/cclTokenedEnterpriseParticipantApi";
import ComponentLoader from "../../../../components/common/componentLoader";
import FlexGrid from "../../../../layouts/flexGrid";
import { Participant } from "../../../../services/types/enterpriseParticipantApiTypes";
import { useGetCclEventByEventKeyQuery } from "../../../../services/cclTokenedSessionApi";
import { getErrorMsg } from "../../../../utilities/rtkQueryErrorHelpers";
import SessionParticipantContactListDataGrid from "./sessionParticipantContactListDataGrid";
import { InventoryDetail } from "../../../../services/types/assessmentAPITypes";

interface SessionParticipantsContactSubPanelProps {
    sessionId: string;
    inventory?: InventoryDetail;
    inventoryIsFetching?: boolean;
}

const SessionParticipantsContactSubPanel: React.FC<SessionParticipantsContactSubPanelProps> = (
    props,
) => {
    const [confirmedParticipants, setConfirmedParticipants] = React.useState<Participant[]>([]);
    const [showAttendance, setShowAttendance] = React.useState<boolean>(true);
    const {
        data: sessionDetails,
        isLoading: detailsLoading,
        isSuccess: detailsSuccess,
        isError: detailsIsError,
        error: detailError,
    } = useGetCclEventByEventKeyQuery(props.sessionId, { skip: !props.sessionId });
    const {
        data: participants,
        isLoading,
        isSuccess,
        isError,
        error,
    } = useGetCclParticipantsByEventKeyQuery(props.sessionId, { skip: !props.sessionId });

    React.useEffect(() => {
        if (sessionDetails != null) {
            const show =
                (sessionDetails?.type.trim() === "Custom" ||
                    sessionDetails?.type.trim() === "Open Enrollment") &&
                sessionDetails?.managingLocation.trim() !== "Licensees";
            setShowAttendance(show);
        }
    }, [sessionDetails]);

    React.useEffect(() => {
        if (participants != null && participants.length > 0) {
            setConfirmedParticipants(participants);
        } else {
            setConfirmedParticipants([]);
        }
    }, [participants]);

    function showErrorAlert(msg: string | undefined) {
        return (
            <div>
                <Alert severity="error" sx={{ fontSize: 14 }}>
                    <AlertTitle sx={{ fontSize: 14 }}>Error</AlertTitle>
                    {msg}
                </Alert>
            </div>
        );
    }

    if (isLoading || detailsLoading) {
        return <ComponentLoader msg={"Loading Registrations"} />;
    }

    if (isSuccess && detailsSuccess && sessionDetails != null) {
        return (
            <Stack spacing={2} sx={{ width: "100%" }}>
                <Card sx={{ width: 1, m: 2, p: 2, flexGrow: 1 }}>
                    <FlexGrid>
                        <SessionParticipantContactListDataGrid
                            participants={confirmedParticipants}
                            showAttendance={showAttendance}
                            session={sessionDetails}
                            inventory={props.inventory}
                            inventoryIsFetching={props.inventoryIsFetching}
                        />
                    </FlexGrid>
                </Card>
            </Stack>
        );
    }

    if (isError) {
        return showErrorAlert(getErrorMsg(error));
    }

    if (detailsIsError) {
        return showErrorAlert(getErrorMsg(detailError));
    }

    return showErrorAlert("Unknown error loading session registrations.");
};

export default SessionParticipantsContactSubPanel;
